/**
 * useActivation — reads the account's activation + value spine.
 *
 * Backed by GET /v1/activation (recomputed out-of-band by the activation-spine
 * cron). The trial→paid funnel UI (ActivationNudge, the /upgrade paywall) reads
 * this to decide what to show and to anchor copy on the real dollars Waypoint
 * has surfaced. Values are a fast cache — empty ({}) until the spine first runs.
 *
 * `activation_state` is a HIGH-WATER MARK and never goes down;
 * `value_metrics.current_state` is where the account stands right now. Copy
 * about what Waypoint is doing for them needs the second one — see
 * ActivationNudge's reconnect branch.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type ActivationState =
  | 'pre_onboard'
  | 'tool_connected'
  | 'first_value'
  | 'activated'
  | 'power_user';

export interface ValueMetrics {
  /** Stage derived from CURRENT signals. `activation_state` is the high-water
   *  mark, so this is the one that drops when a grant dies or the account goes
   *  quiet. Absent until the spine has run since 2026-10-21. */
  current_state?: ActivationState;
  at_risk_clients?: number;
  revenue_at_risk?: number;
  ar_outstanding?: number;
  dollars_surfaced?: number;
  health_predictions?: number;
  active_integrations?: number;
  computed_at?: string;
}

export interface ActivationData {
  activation_state: ActivationState;
  value_metrics: ValueMetrics;
  computed_at: string | null;
}

const DEFAULT: ActivationData = {
  activation_state: 'pre_onboard',
  value_metrics: {},
  computed_at: null,
};

export function useActivation(): ActivationData {
  const { data } = useQuery({
    queryKey: ['activation'],
    queryFn: () => api<ActivationData>('/v1/activation'),
    staleTime: 10 * 60_000, // activation state changes slowly
    retry: 1,
  });
  return data ?? DEFAULT;
}

/** Where the account stands RIGHT NOW. Falls back to the high-water mark for
 *  rows the spine hasn't recomputed since current_state was added. */
export function currentState(d: ActivationData): ActivationState {
  return d.value_metrics?.current_state ?? d.activation_state;
}

/** Headline dollars Waypoint has surfaced for this account (0 until computed). */
export function dollarsSurfaced(d: ActivationData): number {
  const v = d.value_metrics?.dollars_surfaced;
  return typeof v === 'number' && isFinite(v) && v > 0 ? v : 0;
}
